import { useState } from "react";
import { Link } from "react-router-dom";
import type { AnalyzeAnnotation, PassageCheckResponse } from "@edufadder/shared-types";
import { colors, fonts, radii, spacing, stanceLabel } from "@edufadder/ui";
import * as api from "../lib/api.js";
import { ApiError } from "../lib/api.js";
import * as s from "../styles.js";
import { EvidenceChip } from "./EvidenceChip.js";

const SAMPLE = `Every child is different. Some students are visual learners, while others are auditory or kinesthetic learners, so teachers should identify each student's learning style and teach to it.

To get ready for exams, the best way to study is to reread your notes and highlight the key points.

Research shows that testing yourself on the material helps you remember it longer than just reading it again.`;

export function PassageChecker({ compact = false }: { compact?: boolean }): JSX.Element {
  const [text, setText] = useState("");
  const [result, setResult] = useState<PassageCheckResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(value: string): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      setResult(await api.checkPassage(value));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't check that just now. Please try again.");
      setResult(null);
    } finally {
      setBusy(false);
    }
  }

  const byBlock = new Map<string, AnalyzeAnnotation[]>();
  for (const a of result?.annotations ?? []) {
    byBlock.set(a.block_id, [...(byBlock.get(a.block_id) ?? []), a]);
  }

  return (
    <div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim()) void run(text);
        }}
      >
        <label htmlFor="ef-check-text" style={{ ...s.label, fontSize: 15 }}>
          Paste a paragraph from an article, lesson plan, post, or email
        </label>
        <textarea
          id="ef-check-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={8000}
          placeholder="e.g. “Students learn best when lessons match their learning style…”"
          style={{ ...s.textarea, minHeight: compact ? 110 : 170, fontSize: 15, lineHeight: 1.5, background: "#fff" }}
        />
        <div style={{ display: "flex", gap: spacing(3), flexWrap: "wrap", alignItems: "center" }}>
          <button type="submit" style={s.buttonPrimary} disabled={busy || !text.trim()}>
            {busy ? "Checking…" : "Check this passage"}
          </button>
          <button
            type="button"
            style={s.buttonSecondary}
            onClick={() => {
              setText(SAMPLE);
              void run(SAMPLE);
            }}
          >
            Try a sample
          </button>
          <span style={s.mutedText}>Your text is checked in memory and never stored.</span>
        </div>
      </form>

      {error && <p style={{ ...s.errorText, marginTop: spacing(3) }}>{error}</p>}

      {result && (
        <div style={{ marginTop: spacing(5) }} aria-live="polite">
          {result.annotations.length === 0 ? (
            <div style={{ ...s.card, background: colors.goldTint }}>
              <strong>EduFadder didn't notice any of the education ideas it knows about here.</strong>
              <p style={{ margin: `${spacing(2)} 0 0` }}>
                That's by design — it stays quiet unless it's confident. <Link to="/edufads">See which ideas it knows</Link>, or{" "}
                <Link to="/suggest">suggest one it should learn</Link>.
              </p>
            </div>
          ) : (
            <>
              <p style={{ fontWeight: 700, margin: `0 0 ${spacing(3)}` }}>
                EduFadder noticed {new Set(result.annotations.map((a) => a.claim_id)).size} idea
                {new Set(result.annotations.map((a) => a.claim_id)).size === 1 ? "" : "s"} researchers have studied:
              </p>
              {result.blocks.map((block, i) => {
                const notes = byBlock.get(`p${i}`) ?? [];
                return (
                  <div
                    key={i}
                    style={{
                      borderLeft: `4px solid ${notes.length ? colors.gold : colors.border}`,
                      padding: `${spacing(2)} ${spacing(4)}`,
                      marginBottom: spacing(4),
                      background: notes.length ? "#fff" : "transparent",
                      borderRadius: `0 ${radii.md} ${radii.md} 0`,
                    }}
                  >
                    <p style={{ margin: 0, color: notes.length ? colors.ink : colors.muted, lineHeight: 1.6 }}>{block}</p>
                    {notes.map((a) => (
                      <div key={a.claim_id} style={{ marginTop: spacing(3), padding: spacing(3), background: colors.goldTint, borderRadius: radii.md }}>
                        <div style={{ fontFamily: fonts.serif, fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{a.claim_label}</div>
                        <div style={{ ...s.mutedText, fontStyle: "italic", marginBottom: 6 }}>{stanceLabel(a.stance).replace("This page", "This passage")}</div>
                        <EvidenceChip strength={a.evidence_strength} />
                        {a.evidence_summary && <p style={{ margin: `${spacing(2)} 0 0`, lineHeight: 1.55 }}>{a.evidence_summary}</p>}
                        {a.alternative_framing && (
                          <p style={{ margin: `${spacing(2)} 0 0`, lineHeight: 1.55 }}>
                            <strong style={{ color: colors.brand }}>A research-informed approach: </strong>
                            {a.alternative_framing}
                          </p>
                        )}
                        <p style={{ margin: `${spacing(2)} 0 0` }}>
                          <Link to={`/edufads/${a.claim_id}`} style={{ fontWeight: 700 }}>
                            Read the research &amp; sources →
                          </Link>
                        </p>
                      </div>
                    ))}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
